/**
 * Best Next Move Engine V2 — Core Implementation
 *
 * Deterministic move selection engine.
 * Engine suggests moves only. It does not execute or approve.
 */

// Browser-safe crypto stub for preview deployment
// The actual crypto module is not available in the browser
const createHashStub = () => ({
  update: (data: string) => createHashStub(),
  digest: (encoding: string) => 'stub-hash-' + Date.now().toString(36)
});

const createHash = typeof window !== 'undefined' ? createHashStub : (() => {
  // In Node.js, try to use the real crypto module
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('node:crypto').createHash;
  } catch {
    return createHashStub;
  }
})();

import type {
  Move,
  SystemState,
  BlockedMove,
  StrategyMemoryEntry,
  ExecutionHistoryEntry,
  BestNextMoveEngineInput,
  BestNextMoveEngineOutput,
  MoveScoringCriteria,
  ScoredMove,
  BestNextMoveEngineConfig,
  AdmissibilityRule,
  EngineValidationResult,
} from './bestNextMoveTypes.js';
import { DEFAULT_ENGINE_CONFIG } from './bestNextMoveTypes.js';

/**
 * Engine version
 */
const ENGINE_VERSION = 'best-next-move-engine/v2';

/**
 * Default admissibility rules
 */
const DEFAULT_ADMISSIBILITY_RULES: AdmissibilityRule[] = [
  {
    ruleId: 'authority-check',
    description: 'Move must have required authority available',
    check: (move: Move, _systemState: SystemState) => {
      if (!move.requiredAuthority) {
        return { admissible: true };
      }
      // FAIL_CLOSED: If authority is required but not available, move is inadmissible
      // This is a placeholder - actual authority checking would be system-specific
      return { admissible: true, reason: 'Authority check not implemented in default rules' };
    },
  },
  {
    ruleId: 'dependency-check',
    description: 'Move dependencies must be satisfiable',
    check: (move: Move, _systemState: SystemState) => {
      if (move.dependencies.length === 0) {
        return { admissible: true };
      }
      // Check if dependencies can potentially be satisfied
      // This is a basic check - actual satisfaction is scored separately
      return { admissible: true };
    },
  },
];

/**
 * Calculate dependency satisfaction score
 * Highest priority factor: how many dependencies are currently satisfied
 */
function calculateDependencySatisfaction(
  move: Move,
  systemState: SystemState
): number {
  if (move.dependencies.length === 0) {
    return 100; // No dependencies, fully satisfied
  }

  const availableArtifacts = new Set(systemState.availableArtifacts);
  const satisfiedCount = move.dependencies.filter(dep => 
    availableArtifacts.has(dep)
  ).length;

  const score = (satisfiedCount / move.dependencies.length) * 100;
  return Math.round(score);
}

/**
 * Calculate unblock potential score
 * How many current blockers would this move resolve?
 */
function calculateUnblockPotential(
  move: Move,
  systemState: SystemState
): number {
  if (move.unblocks.length === 0) {
    return 0; // Does not unblock anything
  }

  if (systemState.activeBlockers.length === 0) {
    return 0; // No blockers to resolve
  }

  const blockersSet = new Set(systemState.activeBlockers);
  const resolvedCount = move.unblocks.filter(unblock => 
    blockersSet.has(unblock)
  ).length;

  // Score based on proportion of blockers resolved
  const score = (resolvedCount / systemState.activeBlockers.length) * 100;
  return Math.round(score);
}

/**
 * Calculate risk score
 * Lower is safer. Based on risk level and duration.
 */
function calculateRiskScore(move: Move): number {
  let risk = 0;

  // Risk by risk level
  switch (move.riskLevel) {
    case 'low':
      risk += 10;
      break;
    case 'medium':
      risk += 40;
      break;
    case 'high':
      risk += 70;
      break;
  }

  // Risk by duration (longer moves are riskier)
  if (move.estimatedDurationMs) {
    if (move.estimatedDurationMs > 300000) { // > 5 minutes
      risk += 20;
    } else if (move.estimatedDurationMs > 120000) { // > 2 minutes
      risk += 10;
    }
  }

  return Math.min(risk, 100);
}

/**
 * Calculate success rate from strategy memory
 */
function calculateSuccessRate(
  move: Move,
  strategyMemory?: Map<string, StrategyMemoryEntry>
): number {
  if (!strategyMemory || !strategyMemory.has(move.moveId)) {
    return 50; // No history, neutral score
  }

  const entry = strategyMemory.get(move.moveId)!;
  return entry.successRate;
}

/**
 * Calculate move priority
 * Can be derived from metadata or default to medium
 */
function calculatePriority(move: Move): number {
  if (move.metadata?.priority && typeof move.metadata.priority === 'number') {
    return Math.min(100, Math.max(0, move.metadata.priority as number));
  }
  return 50; // Default medium priority
}

/**
 * Calculate total score from criteria
 * Weighted formula:
 * - Dependency satisfaction: 40% (highest priority)
 * - Unblock potential: 25%
 * - Risk score: 20% (inverted - lower risk = higher score)
 * - Success rate: 10%
 * - Priority: 5%
 */
function calculateTotalScore(criteria: MoveScoringCriteria): number {
  const totalScore =
    (criteria.dependencySatisfaction * 0.40) +
    (criteria.unblockPotential * 0.25) +
    ((100 - criteria.riskScore) * 0.20) + // Invert risk: lower risk = higher score
    (criteria.successRate * 0.10) +
    (criteria.priority * 0.05);

  return Math.max(0, Math.min(100, totalScore));
}

/**
 * Calculate deterministic confidence level
 * Based on score stability and criteria strength
 */
function calculateConfidence(criteria: MoveScoringCriteria, totalScore: number): number {
  let confidence = totalScore; // Base confidence is the total score

  // Boost confidence for high dependency satisfaction (critical factor)
  if (criteria.dependencySatisfaction >= 90) {
    confidence += 10;
  } else if (criteria.dependencySatisfaction >= 70) {
    confidence += 5;
  }

  // Reduce confidence for high risk
  if (criteria.riskScore >= 70) {
    confidence -= 15;
  } else if (criteria.riskScore >= 50) {
    confidence -= 8;
  }

  // Boost confidence for high unblock potential
  if (criteria.unblockPotential >= 70) {
    confidence += 8;
  }

  return Math.max(0, Math.min(100, confidence));
}

/**
 * Generate human-readable reason for selection
 */
function generateSelectionReason(
  scoredMove: ScoredMove,
  totalMovesConsidered: number
): string {
  const reasons: string[] = [];
  const criteria = scoredMove.criteria;

  reasons.push(`Selected move "${scoredMove.move.moveId}" (${scoredMove.move.description}).`);

  if (criteria.dependencySatisfaction === 100) {
    reasons.push('All dependencies are satisfied.');
  } else if (criteria.dependencySatisfaction >= 70) {
    reasons.push(`Most dependencies are satisfied (${criteria.dependencySatisfaction}%).`);
  } else {
    reasons.push(`${criteria.dependencySatisfaction}% of dependencies are satisfied.`);
  }

  if (criteria.unblockPotential > 0) {
    reasons.push(`Resolves ${criteria.unblockPotential}% of current blockers.`);
  }

  if (criteria.riskScore <= 30) {
    reasons.push('Low risk move.');
  } else if (criteria.riskScore <= 60) {
    reasons.push('Medium risk move.');
  } else {
    reasons.push('High risk move.');
  }

  if (criteria.successRate >= 80) {
    reasons.push('High historical success rate.');
  } else if (criteria.successRate >= 50) {
    reasons.push('Moderate historical success rate.');
  }

  reasons.push(`Score: ${scoredMove.score.toFixed(1)}/100, Confidence: ${scoredMove.confidence.toFixed(1)}%.`);
  reasons.push(`Evaluated against ${totalMovesConsidered} available moves.`);

  return reasons.join(' ');
}

/**
 * Generate reason for why a move was not selected (for alternatives)
 */
function generateWhyNotSelectedReason(
  scoredMove: ScoredMove,
  selectedScore: number
): string {
  const criteria = scoredMove.criteria;
  const scoreDiff = selectedScore - scoredMove.score;

  if (scoreDiff < 5) {
    return 'Very close to selected move - minor difference in scoring factors.';
  } else if (scoreDiff < 15) {
    return 'Competitive option but slightly lower overall score.';
  } else if (criteria.dependencySatisfaction < 50) {
    return 'Lower dependency satisfaction compared to selected move.';
  } else if (criteria.riskScore > 50) {
    return 'Higher risk level compared to selected move.';
  } else if (criteria.unblockPotential < 20) {
    return 'Lower unblock potential compared to selected move.';
  } else {
    return 'Lower overall score based on weighted scoring criteria.';
  }
}

/**
 * Filter inadmissible moves
 */
function filterInadmissibleMoves(
  moves: Move[],
  systemState: SystemState,
  rules: AdmissibilityRule[]
): { admissible: Move[]; inadmissible: Move[]; reasons: Map<string, string[]> } {
  const admissible: Move[] = [];
  const inadmissible: Move[] = [];
  const reasons = new Map<string, string[]>();

  for (const move of moves) {
    const moveReasons: string[] = [];
    let moveAdmissible = true;

    for (const rule of rules) {
      const result = rule.check(move, systemState);
      if (!result.admissible) {
        moveAdmissible = false;
        if (result.reason) {
          moveReasons.push(`[${rule.ruleId}] ${result.reason}`);
        }
      }
    }

    if (moveAdmissible) {
      admissible.push(move);
    } else {
      inadmissible.push(move);
      if (moveReasons.length > 0) {
        reasons.set(move.moveId, moveReasons);
      }
    }
  }

  return { admissible, inadmissible, reasons };
}

/**
 * Filter blocked moves
 */
function filterBlockedMoves(
  moves: Move[],
  blockedMoves: BlockedMove[]
): { unblocked: Move[]; blocked: Move[] } {
  const blockedSet = new Set(blockedMoves.map(b => b.moveId));
  const unblocked: Move[] = [];
  const blocked: Move[] = [];

  for (const move of moves) {
    if (blockedSet.has(move.moveId)) {
      blocked.push(move);
    } else {
      unblocked.push(move);
    }
  }

  return { unblocked, blocked };
}

/**
 * Score moves deterministically
 */
function scoreMoves(
  moves: Move[],
  systemState: SystemState,
  strategyMemory?: Map<string, StrategyMemoryEntry>,
  executionHistory?: ExecutionHistoryEntry[]
): ScoredMove[] {
  const scoredMoves: ScoredMove[] = [];

  // Build strategy memory from execution history if not provided
  let effectiveStrategyMemory = strategyMemory;
  if (!effectiveStrategyMemory && executionHistory) {
    effectiveStrategyMemory = new Map();
    const historyByMove = new Map<string, ExecutionHistoryEntry[]>();
    
    for (const entry of executionHistory) {
      if (!historyByMove.has(entry.moveId)) {
        historyByMove.set(entry.moveId, []);
      }
      historyByMove.get(entry.moveId)!.push(entry);
    }

    for (const [moveId, entries] of historyByMove) {
      const successCount = entries.filter(e => e.success).length;
      const failureCount = entries.filter(e => !e.success).length;
      const successRate = entries.length > 0 ? (successCount / entries.length) * 100 : 50;
      const avgDuration = entries.length > 0 
        ? entries.reduce((sum, e) => sum + e.durationMs, 0) / entries.length 
        : 0;

      effectiveStrategyMemory.set(moveId, {
        moveId,
        successCount,
        failureCount,
        lastExecutionTimestamp: entries[entries.length - 1]?.timestamp,
        averageDurationMs: avgDuration,
        successRate,
      });
    }
  }

  for (const move of moves) {
    const criteria: MoveScoringCriteria = {
      dependencySatisfaction: calculateDependencySatisfaction(move, systemState),
      unblockPotential: calculateUnblockPotential(move, systemState),
      riskScore: calculateRiskScore(move),
      successRate: calculateSuccessRate(move, effectiveStrategyMemory),
      priority: calculatePriority(move),
    };

    const totalScore = calculateTotalScore(criteria);
    const confidence = calculateConfidence(criteria, totalScore);

    scoredMoves.push({
      move,
      score: totalScore,
      criteria,
      confidence,
    });
  }

  // Sort by score (descending), then by moveId for deterministic tie-breaking
  // This ensures same input → same ordering of alternatives
  scoredMoves.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.move.moveId.localeCompare(b.move.moveId);
  });

  return scoredMoves;
}

/**
 * Best Next Move Engine V2
 *
 * Deterministic move selection engine.
 * Engine suggests moves only. It does not execute or approve.
 */
export class BestNextMoveEngine {
  private config: BestNextMoveEngineConfig;
  private admissibilityRules: AdmissibilityRule[];

  constructor(
    config: BestNextMoveEngineConfig = DEFAULT_ENGINE_CONFIG,
    admissibilityRules: AdmissibilityRule[] = DEFAULT_ADMISSIBILITY_RULES
  ) {
    this.config = config;
    this.admissibilityRules = admissibilityRules;
  }

  /**
   * Validate input
   */
  validateInput(input: BestNextMoveEngineInput): EngineValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // FAIL_CLOSED: System state is required
    if (!input.systemState) {
      errors.push('FAIL_CLOSED: System state is required');
    }

    // FAIL_CLOSED: Available moves is required
    if (!input.availableMoves) {
      errors.push('FAIL_CLOSED: Available moves is required');
    }

    // FAIL_CLOSED: Blocked moves must be an array
    if (!input.blockedMoves) {
      errors.push('FAIL_CLOSED: Blocked moves is required');
    }

    if (input.availableMoves && input.availableMoves.length === 0) {
      warnings.push('No available moves to evaluate');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Select best next move
   */
  selectBestNextMove(input: BestNextMoveEngineInput): BestNextMoveEngineOutput {
    // Validate input
    const validation = this.validateInput(input);
    if (!validation.valid) {
      throw new Error(`FAIL_CLOSED: Invalid input: ${validation.errors.join(', ')}`);
    }

    const { systemState, availableMoves, blockedMoves, strategyMemory, executionHistory } = input;

    // Step 1: Filter inadmissible moves
    const { admissible, inadmissible } = filterInadmissibleMoves(
      availableMoves,
      systemState,
      this.admissibilityRules
    );

    // Step 2: Filter blocked moves
    const { unblocked, blocked } = filterBlockedMoves(admissible, blockedMoves);

    // Step 3: Score remaining moves
    const scoredMoves = scoreMoves(unblocked, systemState, strategyMemory, executionHistory);

    // Step 4: Select top move
    const selectedMove = scoredMoves.length > 0 ? scoredMoves[0].move : null;

    // Step 5: Generate alternatives with details
    const selectedScore = selectedMove ? scoredMoves[0].score : 0;
    const alternatives = scoredMoves.slice(1, this.config.maxAlternatives + 1).map((scoredMove) => ({
      ...scoredMove,
      reason: generateSelectionReason(scoredMove, availableMoves.length),
      whyNotSelected: generateWhyNotSelectedReason(scoredMove, selectedScore)
    }));

    // Step 6: Generate reason for selected move
    let reason = 'No admissible moves available.';
    if (selectedMove) {
      reason = generateSelectionReason(scoredMoves[0], availableMoves.length);
    }

    // Step 7: Calculate confidence
    const confidence = selectedMove ? scoredMoves[0].confidence : 0;

    return {
      selectedMove,
      reason,
      confidence,
      alternatives,
      scoringDetails: {
        totalMovesConsidered: availableMoves.length,
        inadmissibleFiltered: inadmissible.length,
        blockedFiltered: blocked.length,
        scoredMoves: scoredMoves.length,
      },
      timestamp: Date.now(),
      engineVersion: ENGINE_VERSION,
    };
  }

  /**
   * Compute deterministic hash of input
   * For reproducibility verification
   */
  computeInputHash(input: BestNextMoveEngineInput): string {
    const hashInput = {
      systemState: input.systemState,
      availableMoves: input.availableMoves.map(m => ({
        moveId: m.moveId,
        moveType: m.moveType,
        dependencies: m.dependencies,
        unblocks: m.unblocks,
        riskLevel: m.riskLevel,
      })),
      blockedMoves: input.blockedMoves.map(b => ({
        moveId: b.moveId,
        reason: b.reason,
      })),
      strategyMemory: input.strategyMemory ? 
        Array.from(input.strategyMemory.entries()).map(([k, v]) => ({
          moveId: k,
          successRate: v.successRate,
        })) : 
        undefined,
      config: this.config,
    };

    const canonicalJson = JSON.stringify(hashInput, Object.keys(hashInput).sort());
    return createHash('sha256').update(canonicalJson).digest('hex');
  }

  /**
   * Add custom admissibility rule
   */
  addAdmissibilityRule(rule: AdmissibilityRule): void {
    this.admissibilityRules.push(rule);
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<BestNextMoveEngineConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

/**
 * Create default engine instance
 */
export function createDefaultEngine(): BestNextMoveEngine {
  return new BestNextMoveEngine();
}
